import { useEffect, useState } from "react";
import { MapPin, GlobeHemisphereWest, DownloadSimple, Storefront } from "@phosphor-icons/react";
import api, { API } from "@/lib/api";
import { money } from "@/lib/format";
import { Card, Loading } from "@/components/riq";
import { useAdminNav } from "@/pages/adminNav";
import { toast } from "sonner";

const PERIODS = [
  { k: "7d", l: "7 days" }, { k: "30d", l: "30 days" }, { k: "90d", l: "90 days" }, { k: "all", l: "All time" },
];
const COLORS = ["#6E3FFC", "#42C97C", "#F5A623", "#FF6B6B", "#2BB0C4", "#B36EFF", "#FF9F1C", "#E0509E"];
const zar = (v) => money(v, "ZAR");

export default function AdminLocations() {
  const { openReceipts } = useAdminNav();
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    setData(null);
    api.get(`/admin/reports?period=${period}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [period]);
  useEffect(() => { api.get("/admin/users/cities").then((r) => setCities(r.data)).catch(() => {}); }, []);

  const exportCsv = async () => {
    const token = localStorage.getItem("riq_token");
    try {
      const res = await fetch(`${API}/admin/reports/export?section=by_city&period=${period}`, {
        credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `sliply_locations_${period}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export CSV."); }
  };

  if (data === false) return <div className="text-muted-foreground">Could not load locations.</div>;

  const byCity = data?.by_city || [];
  const maxCity = Math.max(1, ...byCity.map((c) => c.gross));
  const maxCityAgg = Math.max(1, ...cities.map((c) => c.gross));

  return (
    <div className="space-y-5" data-testid="admin-locations">
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((p) => (
          <button key={p.k} data-testid={`locations-period-${p.k}`} onClick={() => setPeriod(p.k)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${period === p.k ? "bg-riq-primary text-white" : "border border-riq-border text-neutral-600 hover:border-riq-primary"}`}>
            {p.l}
          </button>
        ))}
      </div>

      {/* Cities (all-time aggregate) */}
      {cities.length > 0 && (
        <Card data-testid="locations-cities">
          <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2 mb-4">
            <GlobeHemisphereWest size={18} weight="duotone" /> Top cities (all time)
          </h3>
          <div className="space-y-2.5">
            {cities.slice(0, 10).map((c, i) => (
              <div key={i}>
                <div className="flex items-center justify-between text-sm">
                  <button data-testid={`location-city-link-${c.city}`} onClick={() => openReceipts({ location: c.city })} className="font-medium text-riq-primary hover:underline text-left" title="Show receipts from this city">{c.city}</button>
                  <span className="text-muted-foreground text-xs">{c.users} user{c.users === 1 ? "" : "s"} · {c.receipts} scans · <b className="text-riq-primary">{zar(c.gross)}</b></span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(c.gross / maxCityAgg) * 100}%`, background: COLORS[i % COLORS.length] }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!data ? <Loading /> : (
        <Card data-testid="locations-detail">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2"><MapPin size={18} weight="duotone" /> By location / branch</h3>
            <button onClick={exportCsv} data-testid="locations-export" className="flex items-center gap-1.5 rounded-full border border-riq-border px-3 py-1.5 text-xs font-medium hover:border-riq-primary text-neutral-600">
              <DownloadSimple size={14} weight="bold" /> CSV
            </button>
          </div>
          {byCity.length ? (
            <div className="space-y-2.5">
              {byCity.slice(0, 15).map((r, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-sm">
                    <button data-testid={`location-link-${r.label}`} onClick={() => openReceipts({ location: r.label })} className="truncate flex-1 font-medium flex items-center gap-1.5 text-left text-riq-primary hover:underline" title="Show receipts from this location"><Storefront size={13} className="text-muted-foreground shrink-0" />{r.label}</button>
                    <span className="text-muted-foreground text-xs mx-2">{r.count}</span>
                    <span className="font-semibold w-24 text-right">{zar(r.gross)}</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full bg-riq-primary" style={{ width: `${(r.gross / maxCity) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="text-sm text-muted-foreground py-6 text-center">No location data for this period.</div>}
        </Card>
      )}
    </div>
  );
}
